import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

/**
 * MarketContext — Phase 5 (Market Layer).
 *
 * Resolution order for the active market ISO2 code:
 *   1. explicit user choice (localStorage 'fursalogia.market')
 *   2. server-side IP suggestion from GET /api/geo/lookup
 *   3. safe default 'AE' (الإمارات)
 *
 * Persistence:
 *   - Anonymous users → localStorage (survives refresh).
 *   - Authenticated user preference persistence is delegated to the
 *     existing auth/profile system (not rebuilt here).
 *
 * IP detection NEVER locks the user — it only seeds the initial value.
 * The Market Switcher can change it anytime.
 */

const STORAGE_KEY = "fursalogia.market";
const DEFAULT_MARKET = { iso2: "AE", market_ar: "الإمارات" };

// Enabled markets — data-only. Ordered for the switcher UI.
// `entitled` reflects Phase 5.5 readiness: today everything is free.
// The shape supports a future entitlements layer without route changes.
export const MARKETS = [
  { iso2: "AE", ar: "الإمارات",   calling: "+971", flag: "🇦🇪", enabled: true, entitled: true  },
  { iso2: "SA", ar: "السعودية",   calling: "+966", flag: "🇸🇦", enabled: true, entitled: true  },
  { iso2: "QA", ar: "قطر",         calling: "+974", flag: "🇶🇦", enabled: true, entitled: true  },
  { iso2: "KW", ar: "الكويت",      calling: "+965", flag: "🇰🇼", enabled: true, entitled: true  },
  { iso2: "BH", ar: "البحرين",     calling: "+973", flag: "🇧🇭", enabled: true, entitled: true  },
  { iso2: "OM", ar: "عُمان",        calling: "+968", flag: "🇴🇲", enabled: true, entitled: true  },
  { iso2: "JO", ar: "الأردن",      calling: "+962", flag: "🇯🇴", enabled: true, entitled: true  },
  { iso2: "EG", ar: "مصر",         calling: "+20",  flag: "🇪🇬", enabled: true, entitled: true  },
  { iso2: "SY", ar: "سوريا",       calling: "+963", flag: "🇸🇾", enabled: true, entitled: true  },
  { iso2: "IQ", ar: "العراق",      calling: "+964", flag: "🇮🇶", enabled: true, entitled: true  },
  { iso2: "MA", ar: "المغرب",      calling: "+212", flag: "🇲🇦", enabled: true, entitled: true  },
];

const byIso = (iso2) => MARKETS.find((m) => m.iso2 === iso2) || null;

const MarketContext = createContext(null);

export function MarketProvider({ children }) {
  const [iso2, setIso2] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved && byIso(saved)?.enabled) return saved;
    } catch { /* fall through */ }
    return DEFAULT_MARKET.iso2;
  });
  const [source, setSource] = useState("default");   // default | ip | user

  // If the user hasn't explicitly picked, ask the server for IP suggestion.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved) { setSource("user"); return; }
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${base}/api/geo/lookup`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const suggested = (d.iso2 || "").toUpperCase();
        if (byIso(suggested)?.enabled) {
          setIso2(suggested);
          setSource(d.detection_method === "default" ? "default" : "ip");
        }
      } catch { /* ignore — default already set */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const setMarket = useCallback((next) => {
    const m = byIso(next);
    if (!m || !m.enabled) return;
    setIso2(m.iso2);
    setSource("user");
    try { window.localStorage.setItem(STORAGE_KEY, m.iso2); } catch { /* private mode */ }
  }, []);

  const value = useMemo(() => {
    const market = byIso(iso2) || byIso(DEFAULT_MARKET.iso2);
    return { iso2, market, markets: MARKETS.filter((m) => m.enabled), source, setMarket };
  }, [iso2, source, setMarket]);

  return <MarketContext.Provider value={value}>{children}</MarketContext.Provider>;
}

export function useMarket() {
  const ctx = useContext(MarketContext);
  if (!ctx) throw new Error("useMarket must be used inside MarketProvider");
  return ctx;
}
